import { useEffect } from 'react';
import { PROCUREMENT_ORDER_SKU_GROUP_ITEMS_PAGE_SIZE } from 'components/procurement/constants';
import { QUERY_KEYS } from 'constants/queryKeys';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';
import {
  fleetVehiclesServices,
  servicePointsServices,
  vehiclesServices,
} from '@services';
import { getCleanedUrlParams, showError } from '@utils';
import {
  FILTER_OPERATOR_ENUMS,
  FILTER_RULE_ENUMS,
  SERVICE_POINT_STATUSES,
} from '@constants';
import {
  getServicePointsMap,
  handleEnums,
  normalizeServicePoints,
  transformVehiclesToGridRows,
} from 'hooks/vehicles/utils';

const SERVICE_POINTS_PAGE_SIZE = 10000;

const useProcurementOrderSkuGroupItems = () => {
  const router = useRouter();
  const { isReady } = router;

  const page = router.query?.page || 1;
  const pageSize =
    router.query?.pageSize || PROCUREMENT_ORDER_SKU_GROUP_ITEMS_PAGE_SIZE;
  const orderBy = router.query?.orderBy || '';
  const order = router.query?.order || '';
  const orderId = router.query?.orderId || '';
  const skuGroupId = router.query?.skuGroupId || '';

  const RULE = [
    {
      field: 'procurement.supplierCarId',
      operator: FILTER_OPERATOR_ENUMS['='],
      type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
      value: [orderId],
    },
    {
      field: 'skuId',
      operator: FILTER_OPERATOR_ENUMS['='],
      type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
      value: [skuGroupId],
    },
  ];

  const skuGroupItemsPayload = {
    type: FILTER_RULE_ENUMS.AndRuleType,
    field: null,
    operator: null,
    value: RULE,
  };

  const params = {
    page,
    pageSize:
      parseInt(pageSize, 10) || PROCUREMENT_ORDER_SKU_GROUP_ITEMS_PAGE_SIZE,
    orderBy,
    order,
  };

  const {
    data: procurementOrderSkuGroupItemsData,
    isLoading: loadingSkuGroupItems,
    refetch: refetchProcurementOrderSkuGroupItems,
  } = useQuery(
    [
      QUERY_KEYS.GET_PROCUREMENT_ORDER_SKU_GROUP_ITEMS,
      params,
      orderId,
      skuGroupId,
    ],
    () => {
      const cleanedParams = getCleanedUrlParams({
        orderBy,
        order,
        page,
        pageSize:
          parseInt(pageSize, 10) || PROCUREMENT_ORDER_SKU_GROUP_ITEMS_PAGE_SIZE,
      });
      return fleetVehiclesServices?.getFilteredVehicles({
        params: cleanedParams,
        payload: skuGroupItemsPayload,
      });
    },
    {
      enabled: isReady && !!orderId && !!skuGroupId,
      refetchOnMount: false,
    }
  );

  const servicePointsResponse = useQuery(
    [QUERY_KEYS.GET_SERVICE_POINTS_KEY],
    () => {
      const payload = {
        page: 1,
        pageSize: SERVICE_POINTS_PAGE_SIZE,
        type: 0,
        field: null,
        operator: null,
        value: [
          {
            field: 'status',
            operator: 0,
            type: 5,
            value: SERVICE_POINT_STATUSES.active, // fetch only activated
          },
        ],
      };
      return servicePointsServices.getServicePoints(payload);
    }
  );

  const fetchFleetVehiclesEnums = async () => {
    const results = await Promise.all([
      fleetVehiclesServices.getVehicleSourcingTypes(),
      fleetVehiclesServices.getVehicleDealers(),
      fleetVehiclesServices.getVehicleColors(),
      fleetVehiclesServices.getInsurances(),
      vehiclesServices.getVehiclesEnums(),
    ]);

    return {
      sourcingTypes: results[0],
      dealers: results[1],
      colors: results[2],
      insurances: results[3],
      vehiclesEnums: results[4],
    };
  };

  const fleetVehiclesEnumsResponse = useQuery(
    [QUERY_KEYS.GET_FLEET_VEHICLES_ENUMS_KEY],
    fetchFleetVehiclesEnums,
    { enabled: isReady && !!orderId && !!skuGroupId }
  );

  const total = procurementOrderSkuGroupItemsData?.meta?.total;

  useEffect(() => {
    if (procurementOrderSkuGroupItemsData?.error) {
      showError(procurementOrderSkuGroupItemsData.error);
    }
    if (fleetVehiclesEnumsResponse.error) {
      showError(fleetVehiclesEnumsResponse.error);
    }
  }, [
    procurementOrderSkuGroupItemsData?.error,
    fleetVehiclesEnumsResponse.error,
  ]);

  const servicePoints = normalizeServicePoints(
    servicePointsResponse?.data?.data || []
  );
  const servicePointsMap = getServicePointsMap(servicePoints);

  const loading = loadingSkuGroupItems || fleetVehiclesEnumsResponse.isLoading;
  const enums = handleEnums(fleetVehiclesEnumsResponse?.data || {});
  const vehicles = transformVehiclesToGridRows({
    vehicles: procurementOrderSkuGroupItemsData?.data || [],
    enums,
    servicePoints,
  });

  return {
    page,
    total,
    loading,
    vehicles,
    pageSize:
      parseInt(pageSize, 10) || PROCUREMENT_ORDER_SKU_GROUP_ITEMS_PAGE_SIZE,
    servicePoints: servicePointsMap,
    enums,
    getVehicles: refetchProcurementOrderSkuGroupItems,
  };
};

export default useProcurementOrderSkuGroupItems;
